import React from "react";
import {
  Grid,
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  LinearProgress,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";

const formatIstihdamTuru = (v) =>
  v ? v.split("_").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ") : "-";

const formatUcretTuru = (v) => {
  if (v === "net") return "Net Ücret";
  if (v === "brut") return "Brüt Ücret";
  return "-";
};

const formatCinsiyet = (v) => {
  if (!v) return "-";
  if (v === "erkek") return "Erkek";
  if (v === "kadin") return "Kadın";
  return "Diğer";
};

const formatBool = (v) => (v ? "Evet" : "Hayır");

const formatDate = (v) => {
  if (!v) return "-";
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return v;
    return d.toLocaleDateString("tr-TR");
  } catch {
    return v;
  }
};

const formatSalary = (value) => {
  if (!value && value !== 0) return "-";
  const num = Number(value);
  return isNaN(num)
    ? value
    : `${num.toLocaleString("tr-TR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ₺`;
};

const calculateAge = (dob) => {
  if (!dob) return "-";
  const birthDate = new Date(dob);
  if (isNaN(birthDate.getTime())) return "-";

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

const formatCocukDurum = (cocuk) => {
  if (!cocuk?.durum) return "";

  if (cocuk.durum === "okuyor") {
    return cocuk.egitim_seviyesi
      ? `Okuyor (${cocuk.egitim_seviyesi})`
      : "Okuyor";
  }

  if (cocuk.durum === "okumuyor") return "Okumuyor";
  if (cocuk.durum === "calisiyor") return "Çalışıyor";

  return "";
};



const EmployeeInfoTab = ({ employee, loading, error }) => {
  return (
    <>
      {loading && <LinearProgress />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {employee && (
        <Grid container spacing={1} alignItems="stretch">
          {/* GENEL BİLGİLER */}
          <Grid item xs={12} sm={6} md={4}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} mb={2}>
                  Genel Bilgiler
                </Typography>

                {[
                  {
                    label: "Ad Soyad",
                    value: `${employee.ad} ${employee.soyad}`,
                  },
                  {
                    label: "TC",
                    value: employee.tc || "-",
                  },
                  {
                    label: "Cinsiyet",
                    value: formatCinsiyet(employee.cinsiyet),
                  },
                  {
                    label: "Doğum Tarihi",
                    value: formatDate(employee.dogum_tarihi),
                  },
                  {
                    label: "Anne Adı",
                    value: employee.anne_adi || "-",
                  },
                  {
                    label: "Baba Adı",
                    value: employee.baba_adi || "-",
                  },
                  {
                    label: "Kurum",
                    value:
                      employee.kurum?.kurum_adi ||
                      employee.kurum?.ad ||
                      "-",
                  },
                ].map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      py: 0.5,
                    }}
                  >
                    <Typography color="text.secondary">
                      {item.label}
                    </Typography>
                    <Typography>{item.value}</Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>

          {/* İSTİHDAM / İŞ BİLGİLERİ */}
          <Grid item xs={12} sm={6} md={4}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} mb={2}>
                  İstihdam Bilgileri
                </Typography>

                {[
                  {
                    label: "Departman",
                    value: employee.departman?.ad || "-",
                  },
                  {
                    label: "Pozisyon",
                    value: employee.pozisyon?.ad || "-",
                  },
                  {
                    label: "İstihdam Türü",
                    value: formatIstihdamTuru(employee.istihdam_turu),
                  },
                  {
                    label: "İşe Giriş Tarihi",
                    value: formatDate(employee.ise_giris_tarihi),
                  },
                  ...(employee.isten_cikis_tarihi
                    ? [{
                        label: "İşten Çıkış Tarihi",
                        value: formatDate(employee.isten_cikis_tarihi),
                      }]
                    : []),
                  {
                    label: "Ücret Türü",
                    value: formatUcretTuru(employee.ucret_turu),
                  },
                  {
                    label: "Saatlik Ücret",
                    value: formatSalary(employee.saatlik_ucret),
                  },
                  {
                    label: "Maaş",
                    value: formatSalary(employee.maas),
                  },
                ].map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      py: 0.5,
                    }}
                  >
                    <Typography color="text.secondary">
                      {item.label}
                    </Typography>
                    <Typography>{item.value}</Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>

          {/* AİLE / DURUM BİLGİLERİ */}
          <Grid item xs={12} sm={6} md={4}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} mb={2}>
                  Aile ve Durum Bilgileri
                </Typography>

                {/* Mevcut aile bilgileri */}
                {[
                  { label: "Evli mi?", value: formatBool(employee.evli_mi) },
                  { label: "Çocuk Sayısı", value: employee.cocuk_sayisi ?? "-" },
                  { label: "Çocuk Yardımı", value: formatBool(employee.cocuk_yardimi_aliyor_mu) },
                  { label: "Emekli mi?", value: formatBool(employee.emekli_mi) },
                  { label: "Kamuda Aktif Görevli mi?", value: formatBool(employee.kamuda_aktif_gorevli_mi) },
                  { label: "Engelli mi?", value: formatBool(employee.engelli_mi) },
                ].map((item) => (
                  <Box key={item.label} sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                    <Typography color="text.secondary">{item.label}</Typography>
                    <Typography>{item.value}</Typography>
                  </Box>
                ))}

                {/* Çocuk bilgilerini göster */}
                {employee.cocuklar?.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Çocuklar
                    </Typography>

                    {employee.cocuklar.map((cocuk, i) => {
                      const durumText = formatCocukDurum(cocuk);

                      return (
                        <Box
                          key={cocuk.id}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            py: 0.5,
                            gap: 1,
                          }}
                        >
                          <Typography>
                            {`${i + 1}. Çocuk`}
                          </Typography>

                          <Typography sx={{ textAlign: "right" }}>
                            {formatDate(cocuk.dogum_tarihi)} (
                            {calculateAge(cocuk.dogum_tarihi)} yaş)
                            {durumText && ` — ${durumText}`}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}

              </CardContent>
            </Card>
          </Grid>


          {/* İLETİŞİM / ADRES */}
          <Grid item xs={12} sm={6} md={4}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} mb={2}>
                  İletişim Bilgileri
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <EmailIcon sx={{ fontSize: 18, color: "gray", mr: 1 }} />
                  <Typography>{employee.mail || "-"}</Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <PhoneIcon sx={{ fontSize: 18, color: "gray", mr: 1 }} />
                  <Typography>{employee.telefon || "-"}</Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <LocationOnIcon
                    sx={{
                      fontSize: 18,
                      color: "gray",
                      mr: 1,
                      mt: "2px",
                    }}
                  />
                  <Typography>{employee.adres || "-"}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default EmployeeInfoTab;
